import React, { useState, useEffect } from 'react';
import './DateInput.css';

const MONTHS = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const DateInput = ({ value, onChange, label, monthIndex, year, type = 'start' }) => {
  // Parsear la fecha actual o usar el mes/año por defecto
  const parseDate = () => {
    if (value) {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        return {
          day: date.getDate(),
          month: date.getMonth(),
          year: date.getFullYear()
        };
      }
    }
    // Si no hay valor válido, usar el mes/año por defecto
    return {
      day: 1,
      month: monthIndex,
      year: year
    };
  };

  const [dateState, setDateState] = useState(parseDate());
  const [isEditingDay, setIsEditingDay] = useState(false);
  const [dayInputValue, setDayInputValue] = useState('');
  const [isInternalUpdate, setIsInternalUpdate] = useState(false);

  // Actualizar estado cuando cambia el valor externo (solo si no estamos editando)
  useEffect(() => {
    // No actualizar si el usuario está editando o si fue una actualización interna
    if (isEditingDay || isInternalUpdate) {
      setIsInternalUpdate(false);
      return;
    }

    if (value) {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        const newState = {
          day: date.getDate(),
          month: date.getMonth(),
          year: date.getFullYear()
        };
        // Solo actualizar si realmente cambió
        if (newState.day !== dateState.day || 
            newState.month !== dateState.month || 
            newState.year !== dateState.year) {
          setDateState(newState);
          setDayInputValue('');
          setIsEditingDay(false);
        }
      }
    } else {
      // Si no hay valor, inicializar con el mes/año por defecto
      const defaultState = {
        day: 1,
        month: monthIndex,
        year: year
      };
      if (defaultState.month !== dateState.month || defaultState.year !== dateState.year) {
        setDateState(defaultState);
        setDayInputValue('');
        setIsEditingDay(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const daysInCurrentMonth = new Date(dateState.year, dateState.month + 1, 0).getDate();

  const updateDate = (newDay, newMonth, newYear) => {
    const daysInNewMonth = new Date(newYear, newMonth + 1, 0).getDate();
    const clampedDay = Math.max(1, Math.min(newDay, daysInNewMonth));
    
    const newDate = new Date(newYear, newMonth, clampedDay);
    const dateString = newDate.toISOString().split('T')[0];
    
    setIsInternalUpdate(true);
    setDateState({
      day: clampedDay,
      month: newMonth,
      year: newYear
    });
    
    onChange(dateString);
  };

  const handleDayInputChange = (e) => {
    const inputValue = e.target.value.replace(/[^0-9]/g, ''); // Solo números
    setDayInputValue(inputValue);
    setIsEditingDay(true);
  };

  const handleDayInputBlur = () => {
    setIsEditingDay(false);
    
    if (dayInputValue !== '') {
      const numValue = parseInt(dayInputValue, 10);
      if (!isNaN(numValue) && numValue > 0) {
        const daysInMonth = new Date(dateState.year, dateState.month + 1, 0).getDate();
        const clampedValue = Math.max(1, Math.min(numValue, daysInMonth));
        updateDate(clampedValue, dateState.month, dateState.year);
      } else {
        // Si el valor no es válido, restaurar el valor anterior
        setDayInputValue('');
      }
    } else {
      // Si está vacío, restaurar el valor anterior
      setDayInputValue('');
    }
  };

  const handleDayInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleDayInputBlur();
    }
  };

  const incrementDay = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const newDay = dateState.day + 1;
    const daysInMonth = new Date(dateState.year, dateState.month + 1, 0).getDate();
    
    if (newDay > daysInMonth) {
      // Pasar al siguiente mes
      if (dateState.month === 11) {
        updateDate(1, 0, dateState.year + 1);
      } else {
        updateDate(1, dateState.month + 1, dateState.year);
      }
    } else {
      updateDate(newDay, dateState.month, dateState.year);
    }
  };

  const decrementDay = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const newDay = dateState.day - 1;
    
    if (newDay < 1) {
      // Pasar al mes anterior
      if (dateState.month === 0) {
        const daysInPrevMonth = new Date(dateState.year - 1, 12, 0).getDate();
        updateDate(daysInPrevMonth, 11, dateState.year - 1);
      } else {
        const daysInPrevMonth = new Date(dateState.year, dateState.month, 0).getDate();
        updateDate(daysInPrevMonth, dateState.month - 1, dateState.year);
      }
    } else {
      updateDate(newDay, dateState.month, dateState.year);
    }
  };

  const handleMonthChange = (e) => {
    const newMonth = parseInt(e.target.value, 10);
    const daysInNewMonth = new Date(dateState.year, newMonth + 1, 0).getDate();
    const clampedDay = Math.min(dateState.day, daysInNewMonth);
    updateDate(clampedDay, newMonth, dateState.year);
  };

  const handleYearChange = (e) => {
    const newYear = parseInt(e.target.value, 10);
    const daysInNewMonth = new Date(newYear, dateState.month + 1, 0).getDate();
    const clampedDay = Math.min(dateState.day, daysInNewMonth);
    updateDate(clampedDay, dateState.month, newYear);
  };

  const currentMonthName = MONTHS[dateState.month];
  const daysInMonth = new Date(dateState.year, dateState.month + 1, 0).getDate();

  return (
    <div className={`date-input-wrapper ${type}`}>
      <label className="date-label">{label}</label>
      <div className="date-input-container">
        <div className="date-display">
          <div className="date-day-input-wrapper">
            <button
              type="button"
              className="date-arrow-btn date-arrow-up"
              onClick={incrementDay}
              aria-label="Incrementar día"
            >
              ▲
            </button>
            <input
              type="text"
              className="date-day-input"
              value={isEditingDay ? dayInputValue : String(dateState.day).padStart(2, '0')}
              onChange={handleDayInputChange}
              onBlur={handleDayInputBlur}
              onKeyDown={handleDayInputKeyDown}
              onFocus={() => {
                setIsEditingDay(true);
                setDayInputValue(String(dateState.day));
              }}
              placeholder="01"
              maxLength="2"
            />
            <button
              type="button"
              className="date-arrow-btn date-arrow-down"
              onClick={decrementDay}
              aria-label="Decrementar día"
            >
              ▼
            </button>
          </div>
          <div className="date-month-year">
            <select
              className="date-month-select"
              value={dateState.month}
              onChange={handleMonthChange}
            >
              {MONTHS.map((month, index) => (
                <option key={index} value={index}>
                  {month}
                </option>
              ))}
            </select>
            <select
              className="date-year-select"
              value={dateState.year}
              onChange={handleYearChange}
            >
              {[year - 1, year, year + 1].map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="date-icon">
          {type === 'start' ? '📅' : '🏁'}
        </div>
      </div>
    </div>
  );
};

export default DateInput;
